import { z } from 'zod';
import { baseMessageSchema } from './_base';

export const MAX_PEER_TUNING_CONTROL_BYTES = 16 * 1024;
const identifier = z.string().min(1).max(128).refine(text => !text.includes('\0'));
const hash = z.string().regex(/^sha256:[a-f0-9]{64}$/);
const counter = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const text = (maximum: number) => z.string().min(1).max(maximum).refine(content => !content.includes('\0'));
const value = z.union([z.number().finite(), text(256), z.boolean(), z.null()]);
const error = z.object({ code: identifier, message: text(2_048) }).strict();
const strategy = identifier.refine(id => ['production', 'required-keyframes', 'loss-repair', 'minimal-instrumentation'].includes(id));
const bounded = (minimum: number, maximum: number) => z.number().finite().min(minimum).max(maximum);

export const tuningTrialContextSchema = z.object({
  version: z.literal(1), sessionId: identifier, trialId: identifier,
  connectionEpoch: counter, commandId: identifier, leaseId: identifier,
  configurationHash: hash,
}).strict();

const identity = z.object({
  sessionId: identifier, trialId: identifier, peerId: identifier,
  connectionEpoch: counter, configRevision: counter,
}).strict();

const settingKey = z.enum([
  'video.codec', 'video.maxBitrateBps', 'video.maxFramerate', 'video.resolutionTier',
  'video.degradationPreference', 'video.temporalLayers', 'video.nackRtx', 'ice.transportPolicy',
  'audio.maxBitrateBps', 'audio.stereo', 'audio.packetTimeMs',
  'receiver.videoJitterBufferTargetMs', 'receiver.videoPlayoutDelayHintMs',
  'receiver.audioJitterBufferTargetMs', 'receiver.audioPlayoutDelayHintMs',
  'recovery.mode', 'recovery.keyframePolicy', 'recovery.lossKeyframeMinIntervalMs',
  'recovery.unhealthyHoldMs', 'recovery.healthyHoldMs',
  'capture.resolutionTier', 'capture.requestedFps',
  'telemetry.windowMs', 'telemetry.statsIntervalMs', 'telemetry.encodedFrameTap',
]);
const hint = bounded(0, 1_000).nullable();
const settingValues: Record<z.infer<typeof settingKey>, z.ZodType> = {
  'video.codec': z.enum(['auto', 'h264', 'h265']),
  'video.maxBitrateBps': bounded(30_000, 50_000_000), 'video.maxFramerate': bounded(1, 120),
  'video.resolutionTier': z.enum(['source', 'top', 'middle', 'bottom']),
  'video.degradationPreference': z.enum(['maintain-framerate', 'maintain-resolution', 'balanced']),
  'video.temporalLayers': z.enum(['auto', 'L1T1', 'L1T3']),
  'video.nackRtx': z.enum(['default', 'disabled']), 'ice.transportPolicy': z.enum(['all', 'relay']),
  'audio.maxBitrateBps': bounded(16_000, 320_000), 'audio.stereo': z.boolean(),
  'audio.packetTimeMs': z.union([z.literal(10), z.literal(20)]),
  'receiver.videoJitterBufferTargetMs': hint, 'receiver.videoPlayoutDelayHintMs': hint,
  'receiver.audioJitterBufferTargetMs': hint, 'receiver.audioPlayoutDelayHintMs': hint,
  'recovery.mode': z.enum(['production', 'fixed-application']),
  'recovery.keyframePolicy': z.enum(['production', 'required-only', 'loss-repair']),
  'recovery.lossKeyframeMinIntervalMs': bounded(0, 30_000),
  'recovery.unhealthyHoldMs': bounded(250, 10_000), 'recovery.healthyHoldMs': bounded(1_000, 120_000),
  'capture.resolutionTier': z.enum(['unchanged', 'top', 'middle', 'bottom']),
  'capture.requestedFps': bounded(1, 240).nullable(),
  'telemetry.windowMs': z.union([z.literal(250), z.literal(500), z.literal(1_000)]),
  'telemetry.statsIntervalMs': z.union([z.literal(250), z.literal(500), z.literal(1_000)]),
  'telemetry.encodedFrameTap': z.boolean(),
};
const patch = z.record(settingKey, value).superRefine((settings, context) => {
  for (const [key, requested] of Object.entries(settings)) {
    if (!settingValues[key as z.infer<typeof settingKey>].safeParse(requested).success) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: [key], message: 'Invalid tuning setting' });
    }
  }
});
const receipt = z.object({
  key: settingKey, requested: value, accepted: value.optional(),
  readback: value.optional(), negotiated: value.optional(),
  status: z.enum(['applied', 'clamped', 'unsupported', 'requires-fresh-stream', 'requires-host-restart', 'failed']),
  reason: text(2_048).optional(),
}).strict().refine(setting => settingValues[setting.key].safeParse(setting.requested).success);

const controlContext = z.object({
  target: identity, commandId: identifier, leaseId: identifier,
  configurationHash: hash, deadlineHostMs: bounded(0, Number.MAX_SAFE_INTEGER),
  strategyId: strategy,
}).strict();
const resolvedConfiguration = z.object({
  version: z.literal(1), settings: patch, strategyId: strategy,
  configurationHash: hash, policyVersion: identifier,
}).strict();

const firstFrame = z.object({
  guestConnectionStartMonotonicMs: bounded(0, Number.MAX_SAFE_INTEGER),
  guestCompositionMonotonicMs: bounded(0, Number.MAX_SAFE_INTEGER),
  guestEndpointConnectionEpoch: counter,
}).strict().refine(frame => frame.guestCompositionMonotonicMs >= frame.guestConnectionStartMonotonicMs);

export const tuningSignalingControlSchema = z.discriminatedUnion('kind', [
  z.object({ type: z.literal('stream-tuning'), version: z.literal(1), kind: z.literal('prepare'),
    payload: controlContext.extend({ resolvedConfiguration }).strict(), }).strict(),
  z.object({ type: z.literal('stream-tuning'), version: z.literal(1), kind: z.literal('prepared'),
    payload: controlContext.extend({ accepted: z.boolean(), settings: z.array(receipt).max(25),
      error: error.optional() }).strict().refine(payload => !payload.accepted || payload.error === undefined), }).strict(),
  z.object({ type: z.literal('stream-tuning'), version: z.literal(1), kind: z.literal('commit'),
    payload: controlContext, }).strict(),
  z.object({ type: z.literal('stream-tuning'), version: z.literal(1), kind: z.literal('resume'),
    payload: controlContext.extend({ stage: z.enum(['connected', 'first-frame']),
      settings: z.array(receipt).max(25), firstFrame: firstFrame.optional() }).strict()
      .refine(payload => payload.stage === 'first-frame' ? payload.firstFrame !== undefined : payload.firstFrame === undefined),
  }).strict(),
  z.object({ type: z.literal('stream-tuning'), version: z.literal(1), kind: z.literal('abort'),
    payload: controlContext.extend({ reason: text(2_048) }).strict(), }).strict(),
]).superRefine((control, context) => {
  if (control.kind === 'prepare' && (control.payload.configurationHash !== control.payload.resolvedConfiguration.configurationHash
    || control.payload.strategyId !== control.payload.resolvedConfiguration.strategyId)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Preparation configuration identity differs' });
  }
  if (new TextEncoder().encode(JSON.stringify(control)).byteLength > MAX_PEER_TUNING_CONTROL_BYTES - 1024) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Tuning control exceeds signaling budget' });
  }
});

export const peerTuningControlPayloadSchema = z.object({
  fromId: identifier, toId: identifier, tuningTrial: tuningTrialContextSchema,
  control: tuningSignalingControlSchema,
});
export const peerTuningControlMessageSchema = baseMessageSchema.extend({
  type: z.literal('PEER_TUNING_CONTROL'),
}).merge(peerTuningControlPayloadSchema);

export type TuningTrialContext = z.infer<typeof tuningTrialContextSchema>;
export type TuningSignalingControl = z.infer<typeof tuningSignalingControlSchema>;
export type PeerTuningControlPayload = z.infer<typeof peerTuningControlPayloadSchema>;
export type PeerTuningControlMessage = z.infer<typeof peerTuningControlMessageSchema>;

/** Validates the whole envelope and binds its context to the prepared control identity. */
export function parsePeerTuningControl(value: unknown): PeerTuningControlMessage | undefined {
  const parsed = peerTuningControlMessageSchema.safeParse(value);
  if (!parsed.success) return undefined;
  try {
    if (new TextEncoder().encode(JSON.stringify(value)).byteLength > MAX_PEER_TUNING_CONTROL_BYTES) return undefined;
  } catch { return undefined; }
  const message = parsed.data;
  const context = message.tuningTrial;
  const payload = message.control.payload;
  if (context.sessionId !== payload.target.sessionId || context.trialId !== payload.target.trialId
    || context.connectionEpoch !== payload.target.connectionEpoch || context.commandId !== payload.commandId
    || context.leaseId !== payload.leaseId || context.configurationHash !== payload.configurationHash) return undefined;
  return message;
}
